import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LuPhone, LuMail, LuClock, LuArrowUp } from 'react-icons/lu';
import { useLanguage } from '../../contexts/LanguageContext';
import { PillButton, PharmacyCross } from '../Home/landingUi';
import { SandDecor, SAND_GRADIENT } from './PageKit';

const navigation = (t) => ({
  contact: {
    name: t('banner.pharmacyName'),
    phone: '+41 61 691 74 88',
    email: 'info@dreirosenapotheke.ch',
    hours: 'Mo-Fr 08:00-19:30 / Sa: 08:00 – 17:00'
  },
  solutions: [
    { name: t('footer.links.shop'), href: '/products' },
    { name: t('footer.links.healthArticles'), href: '/blogs' },
  ],
  support: [
    { name: t('footer.links.faqs'), href: '/faqs' },
  ],
  company: [
    { name: t('footer.links.aboutUs'), href: '/aboutus' },
    { name: t('footer.links.services'), href: '/services' },
    { name: t('footer.links.career'), href: '/jobs' },
  ],
  legal: [
    { name: t('footer.links.privacy'), href: '/privacypolicy' },
    { name: t('footer.links.terms'), href: '/termsconditions' },
  ],
  social: [
    {
      name: 'Instagram',
      href: 'https://www.instagram.com/dreirosenapotheke/',
      icon: (props) => (
        <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
          <path
            fillRule="evenodd"
            d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"
            clipRule="evenodd"
          />
        </svg>
      ),
    },
  ],
});

export default function Footer() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const nav = navigation(t);
  
  const linkGroups = [
    { title: t('footer.sections.services'), items: nav.solutions },
    { title: t('footer.sections.support'), items: nav.support },
    { title: t('footer.sections.company'), items: nav.company },
    { title: t('footer.sections.legal'), items: nav.legal },
  ];
  const contactCards = [
    { Icon: LuPhone, label: nav.contact.phone, href: 'tel:+41616917488' },
    { Icon: LuMail, label: nav.contact.email, href: `mailto:${nav.contact.email}` },
    { Icon: LuClock, label: nav.contact.hours },
  ];

  return (
    <>
      {/* The page's closing panel, on the site's sand surface (dots, blush,
          faint cross) in the landing page's gutter: white cards for contact,
          red kept for accents — the CTA, icon discs and link hovers. Kept
          compact; columns stack on phones. */}
      <footer className="mb-[calc(-4rem_-_env(safe-area-inset-bottom))] pt-[clamp(1.25rem,2.5vw,2.5rem)] custom:mb-0 lg:px-[clamp(0.75rem,1.6vw,2rem)] lg:pb-[clamp(0.75rem,1.6vw,2rem)]">
        <div
          className="relative overflow-hidden rounded-t-[2rem] px-[clamp(1.25rem,4vw,4rem)] pb-[calc(5.25rem_+_env(safe-area-inset-bottom))] pt-[clamp(1.5rem,3.5vw,3rem)] text-gray-900 custom:pb-6 shadow-[0_-10px_30px_-20px_rgba(17,24,39,0.25)] lg:rounded-[2.5rem] lg:shadow-none lg:ring-1 lg:ring-black/[0.04]"
          style={{ background: SAND_GRADIENT }}
        >
          <SandDecor radius="rounded-t-[2rem] lg:rounded-[2.5rem]" crossClassName="absolute -right-[18%] -top-[6%] h-[55%] w-auto rotate-[12deg] lg:-right-[4%] lg:-top-[20%] lg:h-[120%]" />

          <div className="relative grid grid-cols-1 gap-7 lg:grid-cols-12 lg:gap-10">
            {/* Brand + CTA */}
            <div className="lg:col-span-5">
              <div className="flex items-center gap-4">
              <Link
                to="/"
                className="inline-flex flex-shrink-0 overflow-hidden rounded-[1.1rem] bg-white p-1 ring-1 ring-black/[0.05] shadow-[0_14px_30px_-16px_rgba(17,24,39,0.45)] transition-transform duration-300 hover:-rotate-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-300"
                aria-label={nav.contact.name}
              >
                <img src="/images/resized_newlogo.webp" alt="" width="120" height="96" loading="lazy" className="h-12 w-auto rounded-[0.8rem] lg:h-14" />
              </Link>
              <div className="min-w-0">
                <p className="text-[clamp(1.25rem,4.5vw,1.6rem)] font-extrabold leading-tight tracking-[-0.03em]">
                  {nav.contact.name}
                </p>
                <p className="mt-0.5 text-[13.5px] leading-snug text-gray-500">{t('login.rightSide.subtitle')}</p>
              </div>
              </div>
              <div className="mt-5 flex flex-wrap items-center gap-2.5">
                <PillButton onClick={() => { navigate('/products'); window.scrollTo({ top: 0 }); }}>{t('hero.ctaDiscover')}</PillButton>
                {nav.social.map((item) => (
                  <a
                    key={item.name}
                    href={item.href}
                    rel="noopener noreferrer"
                    target="_blank"
                    aria-label={item.name}
                    className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-white text-gray-700 ring-1 ring-inset ring-black/[0.07] shadow-[0_8px_18px_-12px_rgba(17,24,39,0.5)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-brand hover:text-white hover:ring-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-red-300"
                  >
                    <item.icon aria-hidden="true" className="h-5 w-5" />
                  </a>
                ))}
              </div>
            </div>

            {/* Link columns */}
            <nav className="grid grid-cols-2 gap-x-6 gap-y-6 sm:grid-cols-4 lg:col-span-7" aria-label="Footer">
              {linkGroups.map(({ title, items }) => (
                <div key={title}>
                  <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-400">{title}</h3>
                  <ul className="space-y-2">
                    {items.map((item) => (
                      <li key={item.name}>
                        <Link
                          to={item.href}
                          className="group/link inline-flex items-center gap-1.5 text-[14px] font-medium text-gray-600 transition-colors duration-200 hover:text-brand focus:outline-none focus-visible:underline"
                        >
                          <span className="h-px w-0 bg-brand transition-all duration-300 group-hover/link:w-3" aria-hidden="true" />
                          {item.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </nav>
          </div>

          {/* Contact cards */}
          <div className="relative mt-7 grid grid-cols-1 gap-2 sm:grid-cols-3 sm:gap-2.5 lg:mt-9">
            {contactCards.map(({ Icon, label, href }) => {
              const body = (
                <>
                  <span className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-brand/10 text-brand transition-colors duration-200 group-hover/card:bg-brand group-hover/card:text-white" aria-hidden="true">
                    <Icon className="h-[1.125rem] w-[1.125rem]" strokeWidth={2} />
                  </span>
                  <span className="min-w-0 truncate text-[14px] font-semibold text-gray-800">{label}</span>
                </>
              );
              const cls = 'group/card flex items-center gap-3 rounded-2xl bg-white px-3.5 py-2.5 ring-1 ring-black/[0.05] shadow-[0_14px_30px_-26px_rgba(17,24,39,0.5)] transition-[transform,box-shadow] duration-300';
              return href ? (
                <a key={label} href={href} className={`${cls} hover:-translate-y-0.5 hover:shadow-[0_20px_40px_-24px_rgb(var(--brand)/0.45)] focus:outline-none focus-visible:ring-2 focus-visible:ring-red-300`}>{body}</a>
              ) : (
                <div key={label} className={cls}>{body}</div>
              );
            })}
          </div>

          {/* Bottom bar */}
          <div className="relative mt-6 flex items-center justify-between gap-4 border-t border-black/[0.06] pt-4">
            <p className="flex items-center gap-2 text-xs text-gray-500">
              <PharmacyCross className="h-3.5 w-3.5 text-brand" />
              {t('footer.copyright')}
            </p>
            <button
              type="button"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white text-gray-700 ring-1 ring-inset ring-black/[0.07] shadow-[0_8px_18px_-12px_rgba(17,24,39,0.5)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-brand hover:text-white hover:ring-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-red-300"
              aria-label="Back to top"
            >
              <LuArrowUp className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      </footer>

    </>
  )
}
